import {Environment} from '@coolcolduk/enum';
import {mapStringToEnumValue, throwIfUndefined} from '@coolcolduk/util';

export enum EnvVar {
  MODE = 'MODE',
  SUBSCRIBE = 'VITE_SUBSCRIBE',
  STRIPE_PORTAL = 'VITE_STRIPE_PORTAL',
  CLARITY_PROJECT_ID = 'VITE_CLARITY_PROJECT_ID',
}

/**
 * Get an environment variable
 */
export function getEnvVar(key: EnvVar): string | undefined {
  return import.meta.env[key];
}

/**
 * Get an environment variable as a boolean
 */
export function getEnvVarBool(key: EnvVar, defaultValue: boolean): boolean {
  const value = getEnvVar(key);
  if (value === undefined) {
    return defaultValue;
  }
  return value === 'true';
}

/**
 * Get an environment variable as a number
 */
export function getEnvVarFloat(key: EnvVar, defaultValue: number): number {
  const value = getEnvVar(key);
  if (value === undefined) {
    return defaultValue;
  }
  return parseFloat(value);
}

/**
 * Get an environment variable as an integer
 */
export function getEnvVarInt(key: EnvVar, defaultValue: number): number {
  const value = getEnvVar(key);
  if (value === undefined) {
    return defaultValue;
  }
  return parseInt(value, 10);
}

// Lazy initialization to avoid chunk loading order issues
let _env: {
  mode: Environment;
  subscribe: boolean;
  clarityProjectId: string;
} | null = null;

function getEnv() {
  if (!_env) {
    _env = {
      /** determines the environment the app is running in */
      mode: throwIfUndefined(mapStringToEnumValue(Environment, getEnvVar(EnvVar.MODE) || ''), 'MODE is not set'),
      /** determines if the subscription feature is enabled */
      subscribe: getEnvVarBool(EnvVar.SUBSCRIBE, false),
      /** Microsoft Clarity project ID for user analytics */
      clarityProjectId: throwIfUndefined(getEnvVar(EnvVar.CLARITY_PROJECT_ID), 'CLARITY_PROJECT_ID is not set'),
    };
  }
  return _env;
}

export const env = {
  get mode() {
    return getEnv().mode;
  },
  get subscribe() {
    return getEnv().subscribe;
  },
  get clarityProjectId() {
    return getEnv().clarityProjectId;
  },
};
